import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../../_LangContext';
import { mergeClasses } from '@griffel/react';
import WaveSurfer from 'wavesurfer.js';
import Utils from '../../../_Utils';
import { DEBUG_MODE } from '../../../_DebugConfig';

const UIVoiceoverBtn = ({ lang, className, filename, onStateChange }) =>
{
    const { getText } = useLang();
    const [audioState, setAudioState] = useState('stopped');
    const [isLoaded, setIsLoaded] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    
    const waveSurfer = useRef(null);
    const waveformRef = useRef(null);

    className = mergeClasses('sound-btn', 'voiceover-btn', className, !isLoaded ? 'loading' : '');
    const filepath = `${process.env.PUBLIC_URL}/audio/${lang}/${filename}.mp3`;

    // Update playback rate when cookie changes
    useEffect(() =>
    {
        if (DEBUG_MODE)
        {
            const fastPlayback = Utils.getCookie('sound_fast_playback', 'false') === 'true';
            setPlaybackRate(fastPlayback ? 1.75 : 1.0);
        }
    },
    []);

    useEffect(() =>
    {
        // Check if filename matches current language before initializing
        const expectedSuffix = `_${lang}`;
        if (!filename.endsWith(expectedSuffix))
        {
            console.log(`Language mismatch: filename "${filename}" doesn't match current language "${lang}"`);
            return;
        }

        // Initialize WaveSurfer
        waveSurfer.current = WaveSurfer.create(
        {
            container: waveformRef.current,
            waveColor: '#bad9f7',
            progressColor: '#0070E0',
            cursorColor: 'transparent',
            barWidth: 2,
            barRadius: 2,
            responsive: false,
            height: 18,
            normalize: true,
            partialRender: true,
            mediaType: "audio/mpeg",
            backend: 'WebAudio',
        });

        // Only set loading state on ready
        waveSurfer.current.on('ready', () => 
        {
            setIsLoaded(true);
            if (DEBUG_MODE && playbackRate !== 1.0)
            {
                waveSurfer.current.setPlaybackRate(playbackRate);
            }
        });

        // Handle finish event to reset state
        waveSurfer.current.on('finish', () => 
        {
            setAudioState('stopped');
            if (onStateChange) onStateChange('stop', filepath);
        });

        // Load the audio file
        waveSurfer.current.load(filepath);

        // Cleanup
        return () => 
        {
            if (waveSurfer.current) 
            {
                waveSurfer.current.destroy();
                setIsLoaded(false);
            }
        };
    },
    [filepath, filename, lang]);

    // Update playback rate when it changes
    useEffect(() =>
    {
        if (waveSurfer.current && isLoaded && DEBUG_MODE)
        {
            waveSurfer.current.setPlaybackRate(playbackRate);
        }
    },
    [playbackRate]);

    const handlePlay = () =>
    {
        if (!isLoaded) return;

        if (!waveSurfer.current.isPlaying())
        {
            waveSurfer.current.play();
            setAudioState('playing');
            if (onStateChange) onStateChange('play', filepath);
        }
    };

    const handleStop = () =>
    {
        if (!isLoaded) return;
        
        waveSurfer.current.stop();
        setAudioState('stopped');
        if (onStateChange) onStateChange('stop', filepath);
    };

    const handlePlayPause = () =>
    {
        if (!isLoaded) return;
        
        if (!waveSurfer.current.isPlaying())
        {
            waveSurfer.current.play();
            setAudioState('playing');
            if (onStateChange) onStateChange('resume', filepath);
        }
        else
        {
            waveSurfer.current.pause();
            setAudioState('paused');
            if (onStateChange) onStateChange('pause', filepath);
        }
    };

    return (
        <button 
            className={`${className} ${audioState}`} 
            disabled={!isLoaded}
            data-filepath={filepath}
        >
            <div className={`btn-label`} onClick={handlePlay}>
                <p className="icon headphone" />
                <div className="text">
                    {getText('voiceover')}
                </div>
            </div>
            <div className="player">
                <div className="icon-circle pause" onClick={handlePlayPause}>
                    <p className="icon pause" />
                </div>
                <div className="waveform" ref={waveformRef} />
                <div className="icon-circle stop" onClick={handleStop}>
                    <p className="icon stop" />
                </div>
            </div>
        </button>
    );
};

export default UIVoiceoverBtn;